import { useEffect, useRef, useState } from 'react'
import { Bot, Send, UserRound } from 'lucide-react'

import { Button } from '../../../ui/Button/Button'
import { ApiError } from '../../../services/api/client'
import type {
  ConversationOwnershipState,
  ConversationSmsRoute,
} from '../conversation-ownership-contracts'
import type { ConversationOwnershipRepository } from '../conversation-ownership-repository'
import styles from '../ConversationsPage.module.css'

function commandId() {
  return crypto.randomUUID().replaceAll('-', '')
}

const failureCopy: Record<string, string> = {
  OWNERSHIP_CONFLICT: 'Conversation ownership changed. Refresh and try again.',
  OPTED_OUT: 'This patient has opted out of SMS messages.',
  SMS_NOT_AVAILABLE: 'SMS is not available for this conversation.',
  SMS_ROUTE_NOT_AVAILABLE:
    'Staff SMS reply is not available for this conversation yet.',
  SMS_ROUTE_CONFLICT:
    'The active SMS route changed. Refresh the conversation before replying.',
  ROUTING_NOT_READY: 'Clinic SMS routing is not ready.',
  AMBIGUOUS_THREAD: 'The exact SMS thread could not be verified.',
  IDEMPOTENCY_CONFLICT: 'This send request conflicted. Start a new reply.',
  INVALID_MESSAGE: 'Enter a valid text message up to 1,600 characters.',
  // Resume AI or a new text, refused while the last staff text's delivery is
  // still unknown. Flio settles it, not the clinic, and some outcomes are never
  // found, so nothing here promises that it clears (Centereach, 2026-10-30).
  RECONCILIATION_REQUIRED:
    'Flio is still confirming whether the last staff text was sent. Try again in a few minutes. If this doesn’t clear, contact Flio support.',
}

// The stored result of this composer's own text, read back by sending the same
// key again. An unconfirmed text may have been delivered, so staff are told not
// to send it again; a text that wasn't sent can be sent as a new one.
const replayCopy: Record<string, string> = {
  FAILED_TERMINAL:
    'This message wasn’t sent. Review the thread before sending it again.',
  RECONCILIATION_REQUIRED:
    'Flio couldn’t confirm whether this message was sent. Don’t send it again. Its result will show in the thread once Flio confirms it.',
}

function errorCopy(error: unknown, fallback: string) {
  return error instanceof ApiError && error.reason
    ? (failureCopy[error.reason] ?? fallback)
    : error instanceof Error && replayCopy[error.message]
      ? (replayCopy[error.message] ?? fallback)
      : fallback
}

export function ConversationTakeover({
  tenantId,
  aggregateId,
  smsRoute,
  state,
  repository,
  onState,
  onRefresh,
  onRefreshRoute,
}: {
  tenantId: string
  aggregateId: string
  smsRoute: ConversationSmsRoute | null
  state: ConversationOwnershipState
  repository: ConversationOwnershipRepository
  onState: (state: ConversationOwnershipState) => void
  onRefresh: () => Promise<void>
  onRefreshRoute: () => Promise<void>
}) {
  const [draft, setDraft] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const retryKey = useRef<string | null>(null)

  useEffect(() => {
    retryKey.current = null
    setError(null)
  }, [
    tenantId,
    aggregateId,
    state.ownershipState,
    smsRoute?.routeRef,
    smsRoute?.routeRevision,
  ])

  const transition = async (action: 'TAKE_OVER' | 'RETURN_TO_FLIO') => {
    setBusy(true)
    setError(null)
    try {
      onState(
        await repository.transition(
          tenantId,
          aggregateId,
          action,
          state.revision,
          commandId(),
          action === 'TAKE_OVER' ? (smsRoute ?? undefined) : undefined
        )
      )
    } catch (caught) {
      if (
        caught instanceof ApiError &&
        caught.reason === 'SMS_ROUTE_CONFLICT'
      ) {
        retryKey.current = null
      }
      setError(
        errorCopy(
          caught,
          'Conversation ownership changed. Refreshing the latest state…'
        )
      )
      await onRefresh()
      await onRefreshRoute()
    } finally {
      setBusy(false)
    }
  }

  const send = async () => {
    const content = draft.trim()
    if (!smsRoute || !content || content.length > 1600) return
    setBusy(true)
    setError(null)
    retryKey.current ??= commandId()
    try {
      await repository.sendStaffSms(tenantId, aggregateId, {
        expectedOwnershipRevision: state.revision,
        idempotencyKey: retryKey.current,
        routeRef: smsRoute.routeRef,
        routeRevision: smsRoute.routeRevision,
        content,
      })
      retryKey.current = null
      setDraft('')
    } catch (caught) {
      // A text that wasn't sent is finished, so sending again is a new text
      // rather than a replay of the same result. An unconfirmed one keeps its
      // key: sending again only reads its result and never sends it twice.
      if (
        (caught instanceof ApiError &&
          caught.reason === 'SMS_ROUTE_CONFLICT') ||
        (caught instanceof Error && caught.message === 'FAILED_TERMINAL')
      ) {
        retryKey.current = null
      }
      setError(
        errorCopy(
          caught,
          'Message was not accepted. Check takeover, SMS availability, and consent before retrying.'
        )
      )
      await onRefresh()
      await onRefreshRoute()
    } finally {
      setBusy(false)
    }
  }

  if (state.ownershipState === 'AI_ACTIVE') {
    return (
      <div className={styles.takeoverBar}>
        <div>
          <Bot size={17} aria-hidden="true" />
          <span>
            <strong>AI responses active</strong>
            <small>Pause AI before replying as clinic staff.</small>
          </span>
        </div>
        {smsRoute ? (
          <Button
            type="button"
            variant="secondary"
            disabled={busy}
            onClick={() => void transition('TAKE_OVER')}
          >
            Pause AI
          </Button>
        ) : (
          <p>Staff SMS reply isn’t available for this conversation yet.</p>
        )}
        {error ? (
          <p className={styles.takeoverError} role="alert">
            {error}
          </p>
        ) : null}
      </div>
    )
  }
  return (
    <div className={styles.staffComposer}>
      <div className={styles.takeoverHeading}>
        <UserRound size={17} aria-hidden="true" />
        <span>
          <strong>AI responses paused</strong>
          <small>Flio automated SMS replies are paused.</small>
        </span>
        <Button
          type="button"
          variant="ghost"
          disabled={busy}
          onClick={() => void transition('RETURN_TO_FLIO')}
        >
          Resume AI
        </Button>
      </div>
      {!smsRoute ? (
        <p>Staff SMS reply isn’t available for this conversation yet.</p>
      ) : null}
      {smsRoute ? (
        <>
          <label htmlFor="staff-sms-message">Message</label>
          <textarea
            id="staff-sms-message"
            value={draft}
            maxLength={1600}
            disabled={busy}
            onChange={(event) => {
              setDraft(event.target.value)
              setError(null)
              retryKey.current = null
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && !event.shiftKey) {
                event.preventDefault()
                void send()
              }
            }}
            placeholder="Write an SMS reply…"
          />
          <div className={styles.composerActions}>
            <span>{draft.length}/1600</span>
            <Button
              type="button"
              disabled={busy || !draft.trim()}
              onClick={() => void send()}
            >
              <Send size={15} aria-hidden="true" />
              {busy ? 'Sending…' : 'Send'}
            </Button>
          </div>
        </>
      ) : null}
      {error ? (
        <p className={styles.takeoverError} role="alert">
          {error}
        </p>
      ) : null}
    </div>
  )
}
